import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, FormControl, Grid, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography, Chip
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getApartmentUsers, getExpenses, getContributions } from '../services/api';

export default function FlatBills() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [apartmentUsers, setApartmentUsers] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [flatSummary, setFlatSummary] = useState([]);
  const [totals, setTotals] = useState({ totalDue: 0, totalPaid: 0, totalRemaining: 0 });

  useEffect(() => {
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    getApartmentUsers(selectedApartment).then(res => setApartmentUsers(res.data)).catch(() => {});
  }, [selectedApartment]);

  useEffect(() => {
    if (!selectedApartment || apartmentUsers.length === 0) return;

    const fetchData = async () => {
      try {
        const [expRes, contRes] = await Promise.all([
          getExpenses(selectedApartment, month, year),
          getContributions(selectedApartment, month, year)
        ]);

        const expenses = expRes.data || [];
        const contributions = contRes.data || [];
        const totalFlats = apartmentUsers.length;

        // Calculate per-flat dues
        const flatDues = {};
        apartmentUsers.forEach(u => {
          flatDues[u.id] = {
            apartmentUserId: u.id,
            flatNumber: u.flatNumber,
            userName: u.userName,
            equalShare: 0,
            perFlatShare: 0,
            totalDue: 0,
            totalPaid: 0,
            remaining: 0
          };
        });

        // Process expenses
        expenses.forEach(exp => {
          if (exp.approvalStatus !== 'Approved') return;

          if (exp.splitType === 'PerFlat' && exp.lineItems && exp.lineItems.length > 0) {
            // Per-flat: assign each line item's amount to the flat
            exp.lineItems.forEach(li => {
              if (flatDues[li.apartmentUserId]) {
                flatDues[li.apartmentUserId].perFlatShare += li.amount;
              }
            });
          } else {
            // Equal share: divide among all flats
            const share = exp.amount / totalFlats;
            Object.values(flatDues).forEach(f => {
              f.equalShare += share;
            });
          }
        });

        // Process contributions
        contributions.forEach(cont => {
          if (flatDues[cont.apartmentUserId]) {
            flatDues[cont.apartmentUserId].totalPaid += cont.amount;
          }
        });

        // Calculate totals
        const summary = Object.values(flatDues).map(f => ({
          ...f,
          equalShare: Math.round(f.equalShare * 100) / 100,
          perFlatShare: Math.round(f.perFlatShare * 100) / 100,
          totalDue: Math.round((f.equalShare + f.perFlatShare) * 100) / 100,
          totalPaid: Math.round(f.totalPaid * 100) / 100,
          remaining: Math.round((f.equalShare + f.perFlatShare - f.totalPaid) * 100) / 100
        }));

        setFlatSummary(summary);
        setTotals({
          totalDue: summary.reduce((s, f) => s + f.totalDue, 0),
          totalPaid: summary.reduce((s, f) => s + f.totalPaid, 0),
          totalRemaining: summary.reduce((s, f) => s + f.remaining, 0)
        });
      } catch {
        setFlatSummary([]);
      }
    };

    fetchData();
  }, [selectedApartment, apartmentUsers, month, year]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Flat Bills Summary</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Monthly billing summary for each flat — dues vs payments.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Apartment</InputLabel>
            <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
              {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {Array.from({ length: 5 }, (_, i) => <MenuItem key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Total Due</Typography>
              <Typography variant="h6" color="warning.dark">₹{totals.totalDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Total Paid</Typography>
              <Typography variant="h6" color="success.main">₹{totals.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ bgcolor: totals.totalRemaining > 0 ? '#fce4ec' : '#e8f5e9' }}>
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
              <Typography variant="h6" color={totals.totalRemaining > 0 ? 'error' : 'success.main'}>₹{totals.totalRemaining.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Flat-wise Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Flat</strong></TableCell>
              <TableCell><strong>Resident</strong></TableCell>
              <TableCell align="right"><strong>Maintenance Share</strong></TableCell>
              <TableCell align="right"><strong>Per-Flat Charges</strong></TableCell>
              <TableCell align="right"><strong>Total Due</strong></TableCell>
              <TableCell align="right"><strong>Paid</strong></TableCell>
              <TableCell align="right"><strong>Remaining</strong></TableCell>
              <TableCell align="center"><strong>Status</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flatSummary.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>No data for this month</Typography>
                </TableCell>
              </TableRow>
            ) : (
              flatSummary.map(f => (
                <TableRow key={f.apartmentUserId} sx={{ bgcolor: f.remaining > 0 ? '#fff8f8' : 'inherit' }}>
                  <TableCell><strong>{f.flatNumber}</strong></TableCell>
                  <TableCell>{f.userName}</TableCell>
                  <TableCell align="right">₹{f.equalShare.toFixed(2)}</TableCell>
                  <TableCell align="right">₹{f.perFlatShare.toFixed(2)}</TableCell>
                  <TableCell align="right"><strong>₹{f.totalDue.toFixed(2)}</strong></TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>₹{f.totalPaid.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: f.remaining > 0 ? 'error.main' : f.remaining < 0 ? '#1565c0' : 'success.main' }}>
                    {f.remaining > 0 ? `₹${f.remaining.toFixed(2)}` : f.remaining < 0 ? `Advance: ₹${Math.abs(f.remaining).toFixed(2)}` : '₹0.00'}
                  </TableCell>
                  <TableCell align="center">
                    {f.remaining < 0 ? (
                      <Chip label="Advance" size="small" color="info" />
                    ) : f.remaining === 0 ? (
                      <Chip label="Paid" size="small" color="success" />
                    ) : f.totalPaid > 0 ? (
                      <Chip label="Partial" size="small" color="warning" />
                    ) : (
                      <Chip label="Unpaid" size="small" color="error" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
            {/* Totals Row */}
            {flatSummary.length > 0 && (
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell colSpan={4}><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>₹{totals.totalDue.toFixed(2)}</strong></TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}><strong>₹{totals.totalPaid.toFixed(2)}</strong></TableCell>
                <TableCell align="right" sx={{ color: totals.totalRemaining > 0 ? 'error.main' : 'success.main' }}><strong>₹{totals.totalRemaining.toFixed(2)}</strong></TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
